import React, {useEffect, useState} from 'react'
import {Helmet} from 'react-helmet'
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
} from 'firebase/firestore'
import {useAuth} from 'app/modules/auth'
import db from 'services/firebase'
import {ToastContainer} from 'react-toastify'
import {AuditPageTitle} from '_metronic/layout/core/AuditPageData'
import JSZip from 'jszip'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import ConfirmationModal from 'app/components/ConfirmationModal'
import Searchbar from 'app/components/Searchbar'
import {AuditCard, EmptyState} from '../components'
import useAuditContext from 'app/Hooks/useAuditContext'
import DocumentViewerModal from 'app/components/DocumentViewerModal'

const AuditComplete = () => {
  const {currentUser} = useAuth()
  const [audits, setAudits] = useState<any[]>([])
  const [filteredAudits, setFilteredAudits] = useState<any[]>([])
  const [dataroom, setDataroom] = useState<any[]>([])
  const [isDropdownVisible, setDropdownVisible] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [searchLength, setSearchLength] = useState(false)
  const [showEmptyState, setShowEmptyState] = useState<boolean>(false)
  const [activeFrameworks, setActiveFrameworks] = useState<any[]>([])
  const [selectedFramework, setSelectedFramework] = useState('')
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [selectedAuditId, setSelectedAuditID] = useState('')
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedAudit, setSelectedAudit] = useState<any>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showDocumentViewer, setShowDocumentViewer] = useState(false)
  const [selectedFile, setSelectedFile] = useState<{
    url: string
    name: string
  } | null>(null)
  const { currentAuditContext } = useAuditContext()

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setShowEmptyState(true)
    }, 2000)
    return () => clearTimeout(timeoutId)
  }, [])

  useEffect(() => {
    setFilteredAudits(audits)
  }, [audits])

  useEffect(() => {
    const unsubscribe = getActiveFramworks()
    return () => unsubscribe && unsubscribe()
  }, [currentUser])

  const getActiveFramworks = () => {
    const tenantId = currentUser?.tenantId || ''
    const tenantRef = doc(db, 'tenants', tenantId)
    const testsCollectionRef = collection(tenantRef, 'active_frameworks')

    const unsubscribe = onSnapshot(testsCollectionRef, (snapshot) => {
      const frameworksData = snapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
        frameworks: doc.data().frameworks,
      }))
      const frameworks = frameworksData[0]?.frameworks || []
      setActiveFrameworks(frameworks)
    })

    return unsubscribe
  }

  useEffect(() => {
    const tenantId = currentUser?.tenantId || ''
    if (!tenantId) {
      return
    }
    const tenantRef = doc(db, 'tenants', tenantId)
    const PolicyRef = collection(tenantRef, 'audits')

    const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
      let Data = snapshot.docs
        .map((doc) => ({
          ...doc.data(),
          id: doc.id,
          status: doc.data()?.status,
          createdAt: doc.data().createdAt,
          auditor: doc.data().auditor,
        }))
        .filter((data) => data.status === 'Completed')
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      if (currentUser?.role === 'Auditor') {
        Data = Data.filter((audit) => {
          if (!audit.auditor) return false
          if (Array.isArray(audit.auditor)) {
            return audit.auditor.some(auditor => auditor.id === currentUser?.uid || auditor.email === currentUser?.email)
          } else {
            return audit.auditor.id === currentUser?.uid || audit.auditor.email === currentUser?.email
          }
        })
      }
      if (currentAuditContext) {
        Data = Data.filter((audit) => {
          if (!audit.auditor) return false
          if (Array.isArray(audit.auditor)) {
            return audit.auditor.some(auditor => auditor.id === currentAuditContext.auditorId || auditor.email === currentAuditContext.auditorEmail)
          } else {
            return audit.auditor.id === currentAuditContext.auditorId || audit.auditor.email === currentAuditContext.auditorEmail
          }
        })
      }
      setAudits(Data)
    })

    return unsubscribe
  }, [currentAuditContext,currentUser])

  useEffect(() => {
    const tenantId = currentUser?.tenantId || ''
    if (!tenantId) {
      return
    }
    const tenantRef = doc(db, 'tenants', tenantId)
    const PolicyRef = collection(tenantRef, 'dataroom')

    const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
      const Data = snapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      }))
      setDataroom(Data)
    })

    return unsubscribe
  }, [])

  useEffect(() => {
    if (searchTerm === '') {
      setFilteredAudits(audits)
      setSearchLength(false)
    } else {
      const filteredItems = audits.filter(
        (audit) =>
          (typeof audit.auditTitle === 'string' && audit.auditTitle.toLowerCase().includes(searchTerm)) ||
          (Array.isArray(audit.framework) &&
            audit.framework.some(
              (fw: string) => typeof fw === 'string' && fw.toLowerCase().includes(searchTerm)
            ))
      )
      setFilteredAudits(filteredItems)
      setSearchLength(filteredItems.length === 0)
    }
  }, [audits, searchTerm])

  const toggleDropdown = () => {
    setDropdownVisible(!isDropdownVisible)
  }

  const handleDownloadAuditReport = async (auditID, auditDocument) => {
    try {
      if (
        auditDocument &&
        'fileURL' in auditDocument &&
        'fileName' in auditDocument &&
        'fileType' in auditDocument
      ) {
        const response = await fetch(auditDocument.fileURL)
        const fileContent = await response.blob()

        const blob = new Blob([fileContent], {type: auditDocument.fileType})

        const blobUrl = URL.createObjectURL(blob)

        const link = document.createElement('a')
        link.href = blobUrl
        link.download = auditDocument.fileName

        document.body.appendChild(link)

        link.click()

        document.body.removeChild(link)
        URL.revokeObjectURL(blobUrl)
        showSuccessToast({message: 'Audit report downloaded'})
      } else {
        showErrorToast({message: 'Audit report missing'})
      }
    } catch (error) {
      showErrorToast({message: 'Error downloading file'})
    }
  }

  const handleView = (auditDocument) => {
    if (auditDocument.fileURL) {
      const { fileName, fileType, fileURL } = auditDocument
      
      const supportedTypes = [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/png',
        'image/jpeg',
        'image/jpg',
        'image/gif',
        'image/webp',
        'image/svg+xml'
      ]
      
      if (supportedTypes.includes(fileType)) {
        setSelectedFile({
          url: fileURL,
          name: fileName || 'Audit Document'
        })
        setShowDocumentViewer(true)
      } else {
        const link = document.createElement('a')
        link.href = fileURL
        link.download = fileName || 'download'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    } else {
      console.error('No download URL available for this item.')
    }
  }

  const handleDownloadReport = async (auditID, dataroomFolderID, auditEvidenceFolderName) => {
    setDownloadLoading(true)
    setSelectedAuditID(auditID)
    try {
      if (dataroomFolderID) {
        const filterData = dataroom.filter((data) => data.parentFolder === dataroomFolderID)

        if (filterData.length > 0) {
          const zip = new JSZip()

          for (const fileData of filterData) {
            const {downloadUrl, name} = fileData

            const response = await fetch(downloadUrl)

            if (!response.ok) {
              console.error(`Failed to fetch file: ${name}`)
              continue
            }

            const blob = await response.blob()
            zip.file(name || 'file', blob)
          }

          const zipBlob = await zip.generateAsync({type: 'blob'})
          const blobUrl = URL.createObjectURL(zipBlob)

          const link = document.createElement('a')
          link.href = blobUrl
          link.download = `${auditEvidenceFolderName}.zip`
          link.click()

          URL.revokeObjectURL(blobUrl)
          showSuccessToast({message: 'Audit evidence downloaded'})
          setDownloadLoading(false)
        } else {
          setDownloadLoading(false)
        }
      }
    } catch (error) {
      setDownloadLoading(false)
    }
  }

  const handleDelete = (audit) => {
    setSelectedAudit(audit)
    setIsDeleteModalOpen(true)
  }

  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true)
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const policyRef = collection(tenantRef, 'audits')
      const docRef = doc(policyRef, selectedAudit.id)
      await deleteDoc(docRef)
      showSuccessToast({message: `${selectedAudit.framework[0]} has been deleted successfully.`})
    } catch (error) {
      showErrorToast({message: 'An error occurred while deleting the audit evidence.'})
    } finally {
      setIsDeleting(false)
      setIsDeleteModalOpen(false)
      setSelectedAudit(null)
    }
  }

  const handleApplyFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const status = e.target.value

    if (status === 'All') {
      setFilteredAudits(audits)
    } else {
      const filtered = audits.filter((data) => data.framework && data.framework.includes(status))
      setFilteredAudits(filtered)
    }
  }

  return (
    <>
      <Helmet>
        <title>SecureSlate | Audits</title>
      </Helmet>
      <AuditPageTitle
       icon='Audits-icon.svg'
        title='Audits'
        desc={'Track and manage your security audits in one centralized view'}
      />
      <div className='card-header border-0   mb-8'>
        <div className='d-flex justify-content-between align-items-center w-100'>
          <div className='d-flex align-items-center gap-3'>
            <div className='flex-lg-fill'>
              <Searchbar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
            </div>
            <select
              id='status'
              className='form-select'
              aria-label='Select Status'
              style={{width: '170px'}}
              value={selectedFramework}
              onChange={(e) => {
                setSelectedFramework(e.target.value)
                handleApplyFilter(e)
              }}
            >
              <option value='All'>Framework</option>
              {activeFrameworks.map((framework) => (
                <option key={framework} value={framework}>
                  {framework}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      {filteredAudits && filteredAudits.length > 0 ? (
        filteredAudits.map((audit) => {
          return (
            <AuditCard
              key={audit.id}
              audit={audit}
              currentUser={currentUser}
              downloadLoading={downloadLoading}
              selectedAuditId={selectedAuditId}
              // selectedFileName={selectedFileName}
              // onEditAudit={handleOpenModal}
              // onUploadReport={triggerFileInputClick}
              // onMarkComplete={handleMarkAsComplete}
              onDownloadAuditReport={handleDownloadAuditReport}
              onDownloadReport={handleDownloadReport}
              onDelete={handleDelete}
              onToggleDropdown={toggleDropdown}
              onView={handleView}
            />
          )
        })
      ) : (
        <>
          <EmptyState
            type={searchLength ? 'no-results' : 'loading'}
            showEmptyState={showEmptyState}
          />
        </>
      )}
      <ToastContainer />
      {isDeleteModalOpen && (
        <ConfirmationModal
          isModalOpen={isDeleteModalOpen}
          hideModal={() => setIsDeleteModalOpen(false)}
          confirmBtnClickHandler={handleConfirmDelete}
          toDeleteItemTitle={`${selectedAudit?.framework[0]} audit`}
          isLoading={isDeleting}
          title={`Remove ${selectedAudit?.framework[0]} Audit`}
          message={`Are you sure you want to remove ${selectedAudit?.framework[0]} audit? This action cannot be undone.`}
          type='delete'
          confirmButtonText='Remove'
        />
      )}
      
      {/* Document Viewer Modal */}
      <DocumentViewerModal
        show={showDocumentViewer}
        onClose={() => {
          setShowDocumentViewer(false)
          setSelectedFile(null)
        }}
        file={selectedFile}
      />
    </>
  )
}

export default AuditComplete
